import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  FxAlert,
  FxButton,
  FxDocumentTable,
  FxPager,
  FxSearchField,
  FxSegmentedTabs,
  FxSpinner,
  type FxDocumentColumn,
} from '@/components/fx'
import type { RecognitionArm } from '@/types/domain'

import type { CockpitState } from '../useCockpit'
import { BulkBar } from './BulkBar'
import {
  documentRow,
  filterTabs,
  isDocumentFilter,
  pageCountOf,
  queuedDocumentIds,
  searchLoaded,
  type DocumentFilter,
} from './documentsModel'
import {
  PAGE_SIZE,
  useDocumentList,
  useSelection,
  type useDocumentActions,
} from './useDocuments'

/** The eight columns the cockpit artboard draws, minus its per-row kebab —
 *  see the note on `openHref` below. */
const COLUMNS: FxDocumentColumn[] = [
  'select',
  'name',
  'date',
  'status',
  'stage',
  'layout',
  'unclear',
  'action',
]

/**
 * The document table with its filter tabs, its search, its bulk bar and its
 * pager.
 *
 * The four filters are server-side (`status`, `needs_review`), so the number
 * on a tab and the rows behind it come from the same query and cannot
 * disagree. The **search is not**: `GET /documents` has no `q`, so it can
 * only see the 25 rows that are loaded. That is a box which would silently
 * miss almost everything on a 400-document template, so it says what it
 * searches — in its label, and again in a line under it once something is
 * typed.
 */
export function DocumentList({
  cockpit,
  actions,
}: {
  cockpit: CockpitState
  actions: ReturnType<typeof useDocumentActions>
}) {
  const navigate = useNavigate()
  const [filter, setFilter] = useState<DocumentFilter>('alle')
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const selection = useSelection()

  const list = useDocumentList(cockpit, { filter, page })
  const docs = useMemo(() => list.data?.docs ?? [], [list.data])
  const visible = useMemo(() => searchLoaded(docs, search), [docs, search])
  const queued = useMemo(() => queuedDocumentIds(cockpit.activeJobs), [cockpit.activeJobs])

  const total = list.data?.total ?? 0
  const pageCount = pageCountOf(total, PAGE_SIZE)
  const selectedIds = useMemo(() => [...selection.selected], [selection.selected])

  const rows = visible.map((doc) =>
    documentRow(doc, {
      template: cockpit.template,
      queued,
      selected: selection.selected.has(doc.id),
    }),
  )

  function changeFilter(value: string) {
    if (!isDocumentFilter(value)) return
    setFilter(value)
    setPage(1)
    selection.clear()
  }

  function changePage(next: number) {
    setPage(next)
    selection.clear()
  }

  const busy =
    actions.analyse.isPending || actions.runArm.isPending || actions.remove.isPending
  const actionError =
    actions.analyse.error ?? actions.runArm.error ?? actions.remove.error ?? null

  function runArm(arm: RecognitionArm, apply: boolean) {
    actions.runArm.mutate(
      { documentIds: selectedIds, arm, apply },
      { onSuccess: () => selection.clear() },
    )
  }

  return (
    <section className="rounded-sm bg-surface p-4 shadow-raised sm:p-8">
      <h2 className="sr-only">Dokumente dieser Vorlage</h2>

      <div className="mb-5 flex flex-wrap items-center gap-4">
        <FxSegmentedTabs
          aria-label="Dokumente filtern"
          items={filterTabs(cockpit.counts)}
          active={filter}
          onSelect={changeFilter}
          className="gap-2 [&>button]:w-auto [&>button]:flex-none [&>button]:px-4"
        />
        <span className="flex-1" />
        <FxSearchField
          placeholder="Auf dieser Seite suchen"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full sm:w-search-width"
        />
      </div>

      {search.trim() !== '' && (
        // A filter that changes the table silently tells a screen-reader
        // user nothing; the same line on the Archiv screen is a `status`.
        <p role="status" className="mb-3 mt-0 text-small text-text-muted">
          {visible.length} von {docs.length} geladenen Dokumenten auf dieser Seite. Die Suche
          durchsucht nur diese Seite — für alles andere benutzen Sie die Filter oder blättern Sie
          weiter.
        </p>
      )}

      <BulkBar
        count={selectedIds.length}
        canWrite={cockpit.canWrite}
        pending={busy}
        error={actionError}
        onAnalyse={() =>
          actions.analyse.mutate(
            { documentIds: selectedIds },
            { onSuccess: () => selection.clear() },
          )
        }
        onRunArm={runArm}
        onDelete={() =>
          actions.remove.mutate(selectedIds, { onSuccess: () => selection.clear() })
        }
        onClear={selection.clear}
      />

      {list.isLoading ? (
        <FxSpinner label="Dokumente werden geladen …" />
      ) : list.error ? (
        <FxAlert tone="danger" title="Die Dokumente konnten nicht geladen werden">
          <p className="m-0">Möglicherweise ist die Verbindung kurz weg.</p>
          <FxButton variant="outlined" className="mt-4" onClick={() => void list.refetch()}>
            Erneut versuchen
          </FxButton>
        </FxAlert>
      ) : rows.length === 0 ? (
        <EmptyState filter={filter} searching={search.trim() !== ''} />
      ) : (
        <div className={list.isPlaceholderData ? 'opacity-60 transition-opacity' : undefined}>
          <FxDocumentTable
            caption="Dokumente dieser Vorlage"
            columns={COLUMNS}
            dense
            rows={rows}
            columnLabels={{ action: 'Aktion' }}
            onToggleRow={(row, on) => row.id && selection.toggle(row.id, on)}
            onToggleAll={(on) =>
              selection.toggleAll(
                rows.map((row) => row.id).filter((id): id is string => !!id),
                on,
              )
            }
            // A `href` on the row would be a plain `<a>` and reload the whole
            // application; `onOpen` keeps it a client-side navigation. The
            // cost is cmd-click, which needs an additive change in
            // `FxDocumentTable` (a link component, or the event) — reported.
            onOpen={(row) => row.id && navigate(`/templates/${cockpit.templateId}/dokumente/${row.id}`)}
          />
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-end gap-4">
        {total > 0 && (
          <span className="mr-auto text-small text-text-muted">
            {total === 1 ? '1 Dokument' : `${total} Dokumente`}
          </span>
        )}
        {pageCount > 1 && (
          <FxPager page={Math.min(page, pageCount)} pageCount={pageCount} onPageChanged={changePage} />
        )}
      </div>
    </section>
  )
}

/**
 * Nothing to show, told apart: an empty filter is a different situation from
 * an empty form, and a search that found nothing is neither.
 */
function EmptyState({ filter, searching }: { filter: DocumentFilter; searching: boolean }) {
  if (searching) {
    return (
      <p className="my-8 text-center text-body text-text-muted">
        Auf dieser Seite passt kein Dokument zu Ihrer Suche.
      </p>
    )
  }
  const text: Record<DocumentFilter, string> = {
    alle: 'Noch kein Dokument hochgeladen. Über „Dokumente hinzufügen“ oben rechts geht es los.',
    unklar: 'Kein Dokument wartet auf eine Entscheidung.',
    analysiert: 'Noch ist kein Dokument fertig analysiert.',
    keinTreffer: 'Jedes Dokument konnte einer Seite dieser Vorlage zugeordnet werden.',
  }
  return <p className="my-8 text-center text-body text-text-muted">{text[filter]}</p>
}
